import React, { useEffect, useState } from "react";
import {
  Card,
  Select,
  Button,
  Table,
  Space,
  Input,
  Popconfirm,
  message,
  Empty,
  Row,
  Col,
} from "antd";
import SchoolApi from "./api";

const { TextArea } = Input;

const currentAcademicYear = () => {
  const d = new Date();
  const start = d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${start + 1}`;
};

const SchoolStudents = () => {
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);

  const [emailsInput, setEmailsInput] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [academicYear, setAcademicYear] = useState(currentAcademicYear());

  useEffect(() => {
    SchoolApi.listSchools()
      .then((d) => setSchools((d || []).filter((s) => s.status !== 3)))
      .catch(() => message.error("Okullar getirilemedi"));
  }, []);

  const loadBranches = (sid) => {
    SchoolApi.listAllBranches(sid)
      .then((d) => setBranches(d || []))
      .catch(() => message.error("Şubeler getirilemedi"));
  };

  const loadAssigned = (bid) => {
    if (!bid) return;
    setLoading(true);
    SchoolApi.listBranchStudents(bid)
      .then((d) => setAssigned(d || []))
      .catch(() => message.error("Öğrenciler getirilemedi"))
      .finally(() => setLoading(false));
  };

  const onSchoolChange = (sid) => {
    setSchoolId(sid);
    setBranchId(null);
    setBranches([]);
    setAssigned([]);
    loadBranches(sid);
  };

  const onBranchChange = (bid) => {
    setBranchId(bid);
    loadAssigned(bid);
  };

  const doAssign = () => {
    const emails = emailsInput
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (!emails.length) return message.warning("En az bir e-posta girin");

    setAssigning(true);
    SchoolApi.assignStudents(branchId, emails.join(","), academicYear)
      .then((res) => {
        const a = res?.assigned?.length || 0;
        const skip = res?.already_assigned?.length || 0;
        const notFound = res?.emails_not_found || [];
        message.success(`${a} öğrenci atandı${skip ? `, ${skip} zaten atanmıştı` : ""}`);
        if (notFound.length)
          message.warning(`Eşleşmeyen e-posta(lar): ${notFound.join(", ")}`);
        setEmailsInput("");
        loadAssigned(branchId);
      })
      .catch((e) => message.error(e?.response?.data?.message || "Atama başarısız"))
      .finally(() => setAssigning(false));
  };

  const unassign = (row) => {
    SchoolApi.unassignStudent(row.assignment_id)
      .then(() => {
        message.success("Öğrenci çıkarıldı");
        loadAssigned(branchId);
      })
      .catch((e) => message.error(e?.response?.data?.message || "İşlem başarısız"));
  };

  const columns = [
    { title: "#", dataIndex: "student_id", width: 80 },
    {
      title: "Ad Soyad",
      render: (_, r) => `${(`${r.first_name || ""} ${r.last_name || ""}`).trim() || "(isimsiz)"}`,
    },
    { title: "E-posta", dataIndex: "email" },
    { title: "Telefon", dataIndex: "phone", render: (v) => v || "-", width: 140 },
    { title: "Akademik Yıl", dataIndex: "academic_year", width: 130, render: (v) => v || "-" },
    {
      title: "İşlem",
      width: 120,
      render: (_, r) => (
        <Popconfirm title="Öğrenci şubeden çıkarılsın mı?" onConfirm={() => unassign(r)} okText="Evet" cancelText="Hayır">
          <Button size="small" danger>
            Çıkar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="container-fluid p-3">
      <h1 className="h3 mb-3">
        <strong>Öğrenci Ata</strong>
      </h1>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <label>Okul</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Okul seçin"
              optionFilterProp="label"
              value={schoolId}
              onChange={onSchoolChange}
              options={schools.map((s) => ({ value: s.id, label: s.code ? `${s.name} (${s.code})` : s.name }))}
            />
          </Col>
          <Col xs={24} md={10}>
            <label>Sınıf / Şube</label>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder={schoolId ? "Şube seçin" : "Önce okul seçin"}
              optionFilterProp="label"
              disabled={!schoolId}
              value={branchId}
              onChange={onBranchChange}
              options={branches.map((b) => ({
                value: b.branch_id,
                label: `${b.grade_name} / ${b.branch_name}  (${b.student_count} öğr.)`,
              }))}
            />
          </Col>
          <Col xs={24} md={4}>
            <label>Akademik Yıl</label>
            <Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="2025-2026" />
          </Col>
        </Row>
      </Card>

      {!branchId ? (
        <Empty description="Devam etmek için okul ve şube seçin" />
      ) : (
        <>
          <Card title="Öğrenci Ekle" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <TextArea
                rows={3}
                placeholder="Öğrenci e-postalarını virgülle ayırarak girin: ornek1@mail.com, ornek2@mail.com"
                value={emailsInput}
                onChange={(e) => setEmailsInput(e.target.value)}
              />
              <Button type="primary" onClick={doAssign} loading={assigning} disabled={!emailsInput.trim()}>
                E-postalardaki öğrencileri ata
              </Button>
            </Space>
          </Card>

          <Card title={`Şubedeki Öğrenciler (${assigned.length})`}>
            <Table rowKey="assignment_id" size="small" loading={loading} dataSource={assigned} columns={columns} pagination={{ pageSize: 20 }} />
          </Card>
        </>
      )}
    </div>
  );
};

export default SchoolStudents;
